import { useEffect, useState } from 'react';
import isEqual from 'lodash.isequal';
import DatePicker from '../rangepicker/rangepicker';
import { DatePickerMode } from '../rangepicker/types';
import {
  DateInputProps,
  DateInputSize,
  DateInputType,
  PickerPosition,
} from './types';
import DateInputPrimary from './date-input-primary';
import DateInputPseudo from './date-input-pseudo';
import ClickOutsideDetector from '../autocomplete/click-outside-detector';
import usePrevious from '../../hooks/use-previous';

import styles from './date-input.module.css';

export default function DateInput({
  title,
  placeholder,
  value,
  pickerPisition = PickerPosition.LEFT,
  onChange,
  error,
  borderless,
  className = '',
  pickerClassName = '',
  size = DateInputSize.MEDIUM,
  bold,
  type = DateInputType.INPUT,
  withoutCustomSelector = false,
  disabled,
  onReset,
  disabledAfter = new Date(2099, 0, 1),
  startDate = new Date(1920, 0, 1),
  tabIndex,
  birthdayPicker,
  withoutHidePlaceholder = false,
  withValueReset,
  formatDateLong,
  shortcuts,
  fullWidth,
  withTime,
  withTimeSelector,
  withoutIcon,
}: DateInputProps): React.JSX.Element {
  const [inputValue, setInputValue] = useState(value);
  const [datepickerVisible, setDatepickerVisible] = useState(false);
  const [focused, setFocused] = useState(false);

  const prevInputValue = usePrevious(inputValue);

  const handleApply = (result: Date) => {
    if (result) {
      setInputValue(result.toISOString());
    }

    setDatepickerVisible(false);
    setFocused(false);

    if (onChange && result) {
      onChange(result);
    }
  };

  const handleReset = () => {
    setInputValue('');
    setFocused(false);
    setDatepickerVisible(false);

    if (onReset) {
      onReset();
    }
  };

  useEffect(() => {
    if (
      (!inputValue && value) ||
      (inputValue && value && !isEqual(inputValue, value))
    ) {
      setInputValue(value);
    }
  }, [inputValue, value]);

  useEffect(() => {
    if (withValueReset) {
      if (!value && prevInputValue && inputValue) {
        setInputValue('');
      }
    }
  }, [prevInputValue, inputValue, value, withValueReset]);

  let dateInputContent;

  if (type === DateInputType.INPUT) {
    dateInputContent = (
      <DateInputPrimary
        title={title}
        borderless={borderless}
        inputValue={inputValue}
        placeholder={placeholder}
        datepickerVisible={datepickerVisible}
        setDatepickerVisible={setDatepickerVisible}
        disabled={disabled}
        tabIndex={tabIndex}
        focused={focused}
        setFocused={setFocused}
        shortcuts={shortcuts}
        withTime={withTime}
        withoutIcon={withoutIcon}
      />
    );
  } else {
    dateInputContent = (
      <DateInputPseudo
        title={title}
        datepickerVisible={datepickerVisible}
        setDatepickerVisible={setDatepickerVisible}
        inputValue={inputValue}
        placeholder={placeholder}
        disabled={disabled}
        tabIndex={tabIndex}
        focused={focused}
        setFocused={setFocused}
        withoutHidePlaceholder={withoutHidePlaceholder}
        formatDateLong={formatDateLong}
        shortcuts={shortcuts}
        withTime={withTime}
        withoutIcon={withoutIcon}
      />
    );
  }

  return (
    <div
      className={`${styles.dateInputWrapper} ${className} ${
        size === DateInputSize.SMALL ? styles.small : ''
      }
      ${bold ? styles.bold : ''} ${fullWidth ? styles.fullWidth : ''}`}
      onMouseEnter={() => setFocused(true)}
      onMouseLeave={() => setFocused(false)}
    >
      {dateInputContent}
      {error && <div className={styles.dateInputError}>{error}</div>}
      {datepickerVisible && (
        <ClickOutsideDetector setOptionsListVisible={setDatepickerVisible}>
          <div
            className={`${styles.dateInputPicker} ${
              pickerPisition === PickerPosition.RIGHT
                ? styles.dateInputPickerRight
                : styles.dateInputPickerLeft
            } ${pickerClassName}`}
          >
            <DatePicker
              startDate={startDate}
              endDate={new Date(2099, 0, 1)}
              onSingleApply={handleApply}
              mode={DatePickerMode.SINGLE}
              value={inputValue ? [inputValue] : []}
              withoutCustomSelector={withoutCustomSelector}
              onReset={handleReset}
              disabledAfter={disabledAfter}
              birthdayPicker={birthdayPicker}
              setDatepickerVisible={setDatepickerVisible}
              withTime={withTime}
              withTimeSelector={withTimeSelector}
            />
          </div>
        </ClickOutsideDetector>
      )}
    </div>
  );
}
